import { makeObservable, computed, observable, action, runInAction } from 'mobx';
import { CancelToken } from 'axios';
import { Subscription } from 'rxjs';
import { ContractsIngestionService } from '../services';
import { InputBaseModel, ContractComparisonModel, NodeBaseModel } from '../models';
import { SourceStore } from '../../custom_shared/stores';
import {
    ActionParams,
    FetchPreviousContractDataRequest,
    ContractComparisonParameters,
    ConfirmContractComparisonSectionMatchesRequest,
    NodeBaseDto
} from '../types';
import { NodeUtils, InstanceUtils } from '../misc';
import AppStore from './AppStore';
import SessionStore from './SessionStore';
import ActionStore from './ActionStore';
import NodesStore from './NodesStore';
import InputsStore from './InputsStore';
import SettingsStore from './SettingsStore';
import { ContractComparisonFilter } from '../misc/ContractComparisonFilterUtils';

type ContractComparisonFilterOption = {
    label: string;
    value: ContractComparisonFilter;
};

export default class ContractComparisonStore {
    readonly filterOptions: ContractComparisonFilterOption[] = [
        { label: 'All Fields', value: 'all' },
        { label: 'Only with Changes', value: 'onlyWithChanges' },
        { label: 'Without Changes', value: 'withoutChanges' },
        { label: 'Added', value: 'added' },
        { label: 'Removed', value: 'removed' },
        { label: 'Modified', value: 'modified' },
        { label: 'Unsaved Changes', value: 'unsaved' }
    ];

    selectedFilter: ContractComparisonFilter = 'all';

    contractComparisons: Map<string, ContractComparisonModel> = new Map();

    private readonly baseCompanyChangeSubscription: Subscription;

    constructor(
        private readonly appStore: AppStore,
        private readonly sessionStore: SessionStore,
        private readonly actionStore: ActionStore,
        private readonly nodesStore: NodesStore,
        private readonly inputsStore: InputsStore,
        private readonly sourceStore: SourceStore,
        private readonly settingStore: SettingsStore,
        private readonly contractsIngestionService: ContractsIngestionService
    ) {
        makeObservable(this, {
            selectedFilter: observable,
            contractComparisons: observable,
            setSelectedFilter: action,
            resetContractComparisons: action,
            selectedContractComparison: computed,
            selectedFilterOption: computed,
            getOrCreateContractComparison: action,
            isComparisonSummaryConfigured: computed
        });

        this.baseCompanyChangeSubscription =
            this.appStore.baseCompanySegregationStore.baseCompanyChangeSubject.subscribe(() => {
                this.resetContractComparisons();
            });
    }

    private static mergeComparisonNodeDto(contractNode: NodeBaseModel, comparisonNode: NodeBaseModel): NodeBaseDto {
        // Missing comparison children remain removed, while retained renewal children keep their other layers.
        const childNodes = comparisonNode.childNodes.map(child => {
            const contractChild = contractNode.childNodes.find(node => node.id === child.id);

            return contractChild ? ContractComparisonStore.mergeComparisonNodeDto(contractChild, child) : child.dto;
        });

        if (InstanceUtils.isBasicNode(comparisonNode) || InstanceUtils.isSectionsNode(comparisonNode)) {
            return { ...comparisonNode.dto, childNodes };
        }

        if (InstanceUtils.isLayersNode(contractNode) && InstanceUtils.isLayersNode(comparisonNode)) {
            const dto = contractNode.dto;
            dto.layers = contractNode.layers.map(
                layer => comparisonNode.layers.find(candidate => candidate.id === layer.id)?.dto ?? layer.dto
            );
            dto.childNodes = childNodes;

            return dto;
        }

        if (
            InstanceUtils.isLayersWithSectionsNode(contractNode) &&
            InstanceUtils.isLayersWithSectionsNode(comparisonNode)
        ) {
            const dto = contractNode.dto;
            dto.layers = contractNode.layers.map(
                layer => comparisonNode.layers.find(candidate => candidate.id === layer.id)?.dto ?? layer.dto
            );
            dto.childNodes = childNodes;

            return dto;
        }

        return contractNode.dto;
    }

    private static refreshNodeExpansion(node: NodeBaseModel, layerId?: string) {
        if (InstanceUtils.isLayersNode(node) || InstanceUtils.isLayersWithSectionsNode(node)) {
            node.refreshExpandedState(layerId);
        } else {
            node.refreshExpandedState();
        }

        node.childNodes.forEach(child => ContractComparisonStore.refreshNodeExpansion(child, layerId));
    }

    setSelectedFilter(selectedFilter: ContractComparisonFilter) {
        this.selectedFilter = selectedFilter;
    }

    get selectedContractComparison() {
        return Array.from(this.contractComparisons.values()).find(contractComparison => contractComparison.isSelected);
    }

    get selectedFilterOption() {
        return this.filterOptions.find(option => option.value === this.selectedFilter);
    }

    get isComparisonSummaryConfigured() {
        return this.sessionStore.response?.isComparisonSummaryConfigured ?? false;
    }

    resetContractComparisons() {
        this.contractComparisons.forEach(contractComparison => contractComparison.reset());

        runInAction(() => {
            this.selectedFilter = 'all';
        });
    }

    getOrCreateContractComparison(businessIdInput: InputBaseModel, parameters: ContractComparisonParameters = {}) {
        const existingContractComparison = this.contractComparisons.get(businessIdInput.guid);

        if (existingContractComparison) {
            return existingContractComparison;
        }

        const contractComparisonModel = new ContractComparisonModel(this, this.sourceStore, businessIdInput, {
            ...parameters,
            layoutType: this.settingStore.layoutSettings?.type
        });

        this.contractComparisons.set(businessIdInput.guid, contractComparisonModel);

        return contractComparisonModel;
    }

    async fetchPreviousContractData(request: FetchPreviousContractDataRequest, cancelToken?: CancelToken) {
        return await this.contractsIngestionService.fetchPreviousContractData(
            this.appStore.sessionExtension,
            this.appStore.sessionId,
            request,
            cancelToken
        );
    }

    async executeContractComparisonAction(nodes: NodeBaseModel[], actionId: string, actionParams: ActionParams = {}) {
        const runtimeData = ActionStore.getRuntimeData(nodes, this.settingStore.layoutSettings, actionParams);

        return await this.contractsIngestionService.executeContractComparisonAction(
            this.appStore.sessionExtension,
            this.appStore.sessionId,
            actionId,
            runtimeData
        );
    }

    async generateContractComparisonSummary(payload: string, cancelToken?: CancelToken) {
        return await this.contractsIngestionService.generateContractComparisonSummary(
            this.appStore.sessionExtension,
            this.appStore.sessionId,
            payload,
            cancelToken
        );
    }

    async confirmSectionMatches(request: ConfirmContractComparisonSectionMatchesRequest) {
        return await this.contractsIngestionService.confirmContractComparisonSectionMatches(
            this.appStore.sessionExtension,
            this.appStore.sessionId,
            request
        );
    }

    openOrLoadContractComparison(contractComparison: ContractComparisonModel) {
        contractComparison.openOrLoad(NodeUtils.copyNodeDtos(this.nodesStore.nodes)).catch(error => {
            console.error('Error while opening contract comparison: ', error);
        });
    }

    saveContractComparisonChanges(contractComparison: ContractComparisonModel) {
        const nodes = contractComparison.newContractNodes
            .map(comparisonNode => {
                const contractNode = this.nodesStore.nodes.find(node => node.id === comparisonNode.id);

                if (!contractNode) {
                    return null;
                }

                return ContractComparisonStore.mergeComparisonNodeDto(contractNode, comparisonNode);
            })
            .filter((node): node is NodeBaseDto => node !== null);

        this.nodesStore.createOrUpdateNodes(NodeUtils.deepCloneNodeDtos(nodes));

        // Saving reuses existing models, so rerun the initial expansion rule to reveal saved values and required fields.
        contractComparison.newContractNodes.forEach(comparisonNode => {
            const contractNode = this.nodesStore.nodes.find(node => node.id === comparisonNode.id);

            if (contractNode) {
                ContractComparisonStore.refreshNodeExpansion(contractNode, contractComparison.parameters?.layerId);
            }
        });

        this.inputsStore.inputsWithSource.forEach(input => input.fetchSourceOptions());
        this.actionStore.rebuildNodes();
    }

    dispose() {
        this.baseCompanyChangeSubscription.unsubscribe();
        this.contractComparisons.forEach(contractComparison => contractComparison.dispose());

        runInAction(() => {
            this.contractComparisons.clear();
            this.selectedFilter = 'all';
        });
    }
}
